import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Typography
} from '@mui/material';

export interface UsageReportRow {
  user_id: string;
  username?: string;
  email?: string;
  organization: string;
  last_used_date: string;
  total_time_spent_hours: number;
}

interface ReportTableProps {
  data: UsageReportRow[];
  /** When true (e.g. organization is Novo Nordisk), Username and Email columns are shown */
  showUsernameEmail?: boolean;
}

const ReportTable: React.FC<ReportTableProps> = ({ data, showUsernameEmail = false }) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (data.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="body1" color="textSecondary">
          No data found. Please adjust your filters.
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer>
      <Table sx={{ minWidth: 700 }}>
        <TableHead>
          <TableRow sx={{ bgcolor: '#f5f5f5' }}>
            <TableCell
              sx={{
                fontWeight: 600,
                color: '#333',
                borderBottom: '2px solid #ddd'
              }}
            >
              User ID
            </TableCell>
            {showUsernameEmail && (
              <>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    color: '#333',
                    borderBottom: '2px solid #ddd'
                  }}
                >
                  Username
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    color: '#333',
                    borderBottom: '2px solid #ddd'
                  }}
                >
                  Email
                </TableCell>
              </>
            )}
            <TableCell
              sx={{
                fontWeight: 600,
                color: '#333',
                borderBottom: '2px solid #ddd'
              }}
            >
              Organization
            </TableCell>
            <TableCell
              sx={{
                fontWeight: 600,
                color: '#333',
                borderBottom: '2px solid #ddd'
              }}
            >
              Last Used Date
            </TableCell>
            <TableCell
              align="right"
              sx={{
                fontWeight: 600,
                color: '#333',
                borderBottom: '2px solid #ddd'
              }}
            >
              Total Time Spent (hrs)
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((row, index) => (
            <TableRow
              key={row.user_id}
              sx={{
                '&:nth-of-type(odd)': {
                  backgroundColor: '#fafafa'
                },
                '&:hover': {
                  backgroundColor: '#f0f0f0'
                },
                '&:last-child td, &:last-child th': {
                  border: 0
                }
              }}
            >
              <TableCell sx={{ py: 2 }}>{row.user_id}</TableCell>
              {showUsernameEmail && (
                <>
                  <TableCell sx={{ py: 2 }}>{row.username ?? '—'}</TableCell>
                  <TableCell sx={{ py: 2 }}>{row.email ?? '—'}</TableCell>
                </>
              )}
              <TableCell sx={{ py: 2 }}>{row.organization}</TableCell>
              <TableCell sx={{ py: 2 }}>{formatDate(row.last_used_date)}</TableCell>
              <TableCell align="right" sx={{ py: 2 }}>
                {row.total_time_spent_hours.toFixed(2)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ReportTable;